import { getResponsesTableOptions, useHeyApi } from "@platform/api";
import { useQuery } from "@tanstack/react-query";

import { extractErrorMessage } from "../../../../../../utils";

import {
  buildResponsesColDefs,
  buildResponsesRowData,
} from "./buildResponsesGridData";

import type { ColDef } from "ag-grid-community";
import type { ResponsesGridRow } from "./types";

interface UseResponsesTableOptions {
  rfpId: string;
  enabled?: boolean;
}

interface UseResponsesTableResult {
  rowData: ResponsesGridRow[];
  colDefs: ColDef<ResponsesGridRow>[];
  isLoading: boolean;
  errorMessage: string | null;
}

/**
 * Fetches the carrier-by-coverage responses matrix for an RFP and derives
 * AG Grid row data and column definitions from the result.
 *
 * Uses the centro-portal BFF endpoint via the `centroPortalClient`.
 */
export const useResponsesTable = ({
  rfpId,
  enabled = true,
}: UseResponsesTableOptions): UseResponsesTableResult => {
  const { centroPortalClient } = useHeyApi();

  const { data, isLoading, error } = useQuery({
    ...getResponsesTableOptions({
      path: { id: rfpId },
      client: centroPortalClient,
    }),
    enabled: enabled && !!rfpId,
    staleTime: 5 * 60 * 1000,
    select(data) {
      const rowData = buildResponsesRowData(data);
      const colDefs = buildResponsesColDefs(data.coverages);
      return { rowData, colDefs };
    },
  });

  return {
    rowData: data?.rowData ?? [],
    colDefs: data?.colDefs ?? [],
    isLoading,
    errorMessage: error
      ? extractErrorMessage(error, "Failed to load responses table.")
      : null,
  };
};
